import CloseIcon from "@assets/images/close-thin.svg";
import { Drawer, Grid } from "@material-ui/core";
import { Color } from "@material-ui/lab/Alert";
import { useUser } from "@saleor/auth";
import MyTypography from "@saleor/components/MyTypography";
import SnackbarAlert from "@saleor/components/SnackbarAlert";
import { STAFF_UPDATE } from "@saleor/config";
import ModalProductDetail from "@saleor/createOrder/components/ModalProductDetail";
import {
  useAddProductToCustomerOrderMutation,
  useContractQuery,
  useCreateCustomerCartMutation,
  useCustomerCartTotalsQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import ContractInfo from "./ContractInfo";
import TableProducts from "./TableProducts";

interface DrawAddProductProps {
  onClose: () => void;
  open: boolean;
  contractId: string;
  refetchData?: () => void;
  cartId?: string;
  staffUpdate?: number;
  title?: string;
  subTitle?: string;
  cartData?: any;
  customerCart?: any;
  isCustomerCreateFlow?: boolean;
  setVisibleDialogError?: (e: boolean) => void;
  setPopUpContent?: (e: any) => void;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      height: 720,
      background: theme.palette.background.default,
      padding: "3.2rem",
      boxShadow: "0px -8px 24px rgba(0, 0, 0, 0.08)",
      borderTopLeftRadius: 32,
      borderTopRightRadius: 32,
      color: theme.palette.grey[900]
    },
    headerClose: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      "& button": {
        background: "none",
        border: "none",
        padding: 0,
        display: "inline-flex",
        marginLeft: 20,
        "& img": {
          width: 16,
          height: 16
        }
      }
    },
    bodyDraw: {
      padding: "2.4rem 5.6rem"
    },
    bodyTitle: {
      color: theme.palette.info.dark
    },
    mainTitle: {
      fontSize: "2.8rem",
      fontWeight: 700,
      lineHeight: "36px"
    },
    subTitle: {
      fontSize: "2.0rem",
      fontWeight: 400,
      lineHeight: "26px"
    },
    gridTitle: {
      margin: "2.4rem 0 1.6rem"
    },
    snackHeadTitle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      columnGap: "1.2rem"
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    }
  }),
  { name: "DrawAddProduct" }
);

const DrawAddProduct: React.FC<DrawAddProductProps> = props => {
  const {
    onClose,
    open,
    contractId,
    refetchData,
    cartId,
    staffUpdate,
    title,
    subTitle,
    cartData,
    customerCart,
    isCustomerCreateFlow = false,
    setVisibleDialogError,
    setPopUpContent
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const { soldToId } = useUser();
  const [, dispatchAppState] = useAppState();
  const [openModalProduct, setOpenModalProduct] = useState<boolean>(false);
  const [modalProductKey, setModalProductKey] = useState<number>(0);
  const [productIdModal, setProductIdModal] = useState<string>("");
  const [contractMaterialIdModal, setContractMaterialIdModal] = useState<
    string
  >("");
  const [typeAlert, setTypeAlert] = useState<Color>("success");
  const [openAlert, setOpenAlert] = useState<boolean>(false);

  const { data } = useContractQuery({
    displayLoader: true,
    variables: {
      id: contractId
    },
    skip: !open,
    fetchPolicy: "network-only"
  });

  const [updateCartCustomer] = useCreateCustomerCartMutation();

  const [addCustomerOrder] = useAddProductToCustomerOrderMutation();

  const { refetch: refetchCartCustomer } = useCustomerCartTotalsQuery({
    skip: true,
    variables: {
      soldToId
    },
    fetchPolicy: "no-cache"
  });

  const reCountCartCustomer = response => {
    if (response?.data?.customerCartTotals?.totalProducts) {
      const orderCount = response?.data?.customerCartTotals?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  };

  const handleOpenModal = (productId, contractMaterialId) => {
    setProductIdModal(productId);
    setContractMaterialIdModal(contractMaterialId);
    setModalProductKey(prevKey => prevKey + 1);
    setOpenModalProduct(true);
  };

  const handleSuccessAdd = () => {
    refetchData();
  };

  const handleAddToCart = product => {
    if (product?.errorQty) {
      setTypeAlert("error");
      setOpenAlert(true);
      return;
    }

    if (staffUpdate === STAFF_UPDATE.UPDATE_CART) {
      updateCartCustomer({
        variables: {
          input: {
            soldToId,
            customerContractId: data?.contract?.id,
            customerId: data?.contract?.customer.id,
            lines: [
              {
                quantity: product.quantity,
                productId: product.product.id,
                contractMaterialId: product.id
              }
            ]
          }
        }
      }).then(response => {
        if (response?.data?.createCustomerCart?.cart?.id) {
          setTypeAlert("success");
          setOpenAlert(true);
          refetchCartCustomer().then(response => reCountCartCustomer(response));
          handleSuccessAdd();
        } else {
          setTypeAlert("error");
          setOpenAlert(true);
        }
      });
    } else if (staffUpdate === STAFF_UPDATE.UPDATE_ORDER) {
      addCustomerOrder({
        variables: {
          id: cartId,
          input: [
            {
              quantity: product.quantity,
              contractProductId: product.id,
              cartItemId: cartId
            }
          ]
        }
      }).then(response => {
        if (
          response?.data?.addProductToCustomerOrder?.errors[0]?.code ===
          "PRODUCT_GROUP_ERROR"
        ) {
          setVisibleDialogError(true);
          setPopUpContent(prodErrorContent);
          return;
        }
        if (response?.data?.addProductToCustomerOrder?.order?.id) {
          setTypeAlert("success");
          setOpenAlert(true);
          handleSuccessAdd();
        } else {
          setTypeAlert("error");
          setOpenAlert(true);
        }
      });
    }
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  if (!contractId) {
    return null;
  }
  const prodErrorContent = (
    <>
      {" "}
      <MyTypography variant="body2" className={classes.inlineBlock}>
        กรุณาเลือก{" "}
        <MyTypography variant="subtitle2" className={classes.inlineBlock}>
          สินค้ากลุ่มเดียวกัน
        </MyTypography>{" "}
        ในการสร้างคำสั่งซื้อ
      </MyTypography>
    </>
  );
  return (
    <>
      <SnackbarAlert
        severity={typeAlert}
        open={openAlert}
        onClose={handleCloseAlert}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <div className={classes.snackHeadTitle}>
          <MyTypography
            variant="subtitle2"
            style={{ color: "#ffffff", marginRight: "2px" }}
          >
            {staffUpdate === STAFF_UPDATE.UPDATE_ORDER ||
            staffUpdate === STAFF_UPDATE.ADD_ORDER_DOMESTIC
              ? intl.formatMessage({
                  defaultMessage: "Added to order",
                  description: "dialog header"
                })
              : intl.formatMessage({
                  defaultMessage: "Added to cart",
                  description: "dialog header"
                })}
          </MyTypography>
          <MyTypography variant="body3" style={{ color: "#ffffff" }}>
            {typeAlert === "success"
              ? staffUpdate === STAFF_UPDATE.UPDATE_ORDER ||
                staffUpdate === STAFF_UPDATE.ADD_ORDER_DOMESTIC
                ? "เพิ่มสินค้าในคำสั่งซื้อสำเร็จ"
                : "เพิ่มสินค้าลงตะกร้าสำเร็จ"
              : ""}
            {typeAlert === "error"
              ? staffUpdate === STAFF_UPDATE.UPDATE_ORDER ||
                staffUpdate === STAFF_UPDATE.ADD_ORDER_DOMESTIC
                ? "เพิ่มสินค้าในคำสั่งซื้อไม่สำเร็จ"
                : "เพิ่มสินค้าลงตะกร้าไม่สำเร็จ"
              : ""}
          </MyTypography>
        </div>
      </SnackbarAlert>
      <Drawer
        open={open}
        anchor={"bottom"}
        PaperProps={{
          className: classes.root
        }}
      >
        <MyTypography variant={"caption"} className={classes.headerClose}>
          {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
          <button onClick={() => onClose()}>
            <img src={CloseIcon} alt="close-icon" />
          </button>
        </MyTypography>
        <div className={classes.bodyDraw}>
          <div className={classes.bodyTitle}>
            <span className={classes.mainTitle}>{title}</span>{" "}
            <span className={classes.subTitle}>({subTitle})</span>
          </div>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"h7"} className={classes.gridTitle}>
                {intl.formatMessage({ defaultMessage: "รายละเอียด Contract" })}
              </MyTypography>
              <ContractInfo contractData={data?.contract} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"h7"} className={classes.gridTitle}>
                {intl.formatMessage({ defaultMessage: "รายการสินค้า" })} (
                {data?.contract?.products?.length || 0})
              </MyTypography>
              <TableProducts
                cartData={cartData}
                products={data?.contract?.products}
                onClickButton={handleOpenModal}
                onAddToCart={handleAddToCart}
                staffUpdate={staffUpdate}
              />
            </Grid>
          </Grid>
        </div>
      </Drawer>
      <ModalProductDetail
        customerCart={customerCart}
        cartData={cartData}
        contractMaterialId={contractMaterialIdModal}
        key={modalProductKey}
        contractId={contractId}
        productId={productIdModal}
        open={openModalProduct}
        onAddProductComplete={handleSuccessAdd}
        onClose={() => setOpenModalProduct(false)}
        userId={data?.contract?.customer?.id}
        staffUpdate={staffUpdate}
        cartId={cartId}
        isCreateOrderFlow={isCustomerCreateFlow}
        setPopUpContent={setPopUpContent}
        setVisibleDialogError={setVisibleDialogError}
        prodErrorContent={prodErrorContent}
      />
    </>
  );
};

export default DrawAddProduct;
